import * as i0 from '@angular/core';
import { Injectable, Optional, Inject, ViewContainerRef, Component, ViewChild, HostListener, NgModule } from '@angular/core';
import * as i3 from '@upr-web-primer/lock-scroll';
import * as i5 from '@upr-web-primer/storage';
import * as i4 from '@upr-web-primer/viewport';
import { WINDOW } from '@upr-web-primer/window';
import { Subject, Subscription, BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import * as i6 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';

class SimpleModalBroadcaster {
    constructor() {
        this._eventBus = new Subject();
    }
    emit(key, data) {
        this._eventBus.next({ key, data });
    }
    on(key) {
        return this._eventBus.asObservable().pipe(filter(event => event.key === key), map(event => event.data));
    }
}
SimpleModalBroadcaster.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalBroadcaster, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SimpleModalBroadcaster.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalBroadcaster, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalBroadcaster, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SimpleModalHelper {
    constructor(componentRefs, document, componentResolver, appRef, injector) {
        this.componentRefs = componentRefs;
        this.document = document;
        this.componentResolver = componentResolver;
        this.appRef = appRef;
        this.injector = injector;
    }
    dispatchForCode(event) {
        let code;
        if (event.key !== undefined) {
            code = event.key;
        }
        else if (event.keyIdentifier !== undefined) {
            code = event.keyIdentifier;
        }
        else if (event.keyCode !== undefined) {
            code = event.keyCode;
        }
        return code;
    }
    generateId() {
        return (Math.random()
            .toString(36)
            .substring(2, 15) +
            Math.random()
                .toString(36)
                .substring(2, 15));
    }
    createComponent(component, data, container) {
        let componentRef;
        const getComponent = (componentName) => {
            if (this.componentRefs) {
                const firstRef = this.componentRefs[componentName];
                if (firstRef) {
                    return firstRef;
                }
            }
            const factories = Array.from(this.componentResolver['_factories'].keys());
            return factories.find((x) => {
                const matches = /function (.*?)\(.*/g.exec(x + '');
                return (matches &&
                    matches[1] &&
                    matches[1].toLowerCase() === componentName.toLowerCase());
            });
        };
        const passData = (ref) => {
            if (data) {
                for (const x in data) {
                    if (data.hasOwnProperty(x)) {
                        ref.instance[x] = data[x];
                    }
                }
            }
        };
        const factory = this.componentResolver.resolveComponentFactory(typeof component === 'string' ? getComponent(component) : component);
        if (!container) {
            componentRef = factory.create(this.injector);
            passData(componentRef);
            this.appRef.attachView(componentRef.hostView);
            this.document.body.appendChild(componentRef.hostView
                .rootNodes[0]);
        }
        else {
            componentRef = container.createComponent(factory);
            passData(componentRef);
            componentRef.changeDetectorRef.detectChanges();
        }
        return componentRef;
    }
    sizeOf(obj) {
        let size = 0, key;
        for (key in obj) {
            if (obj.hasOwnProperty(key)) {
                size++;
            }
        }
        return size;
    }
    merge(...args) {
        const obj = {};
        args.forEach((val) => {
            for (const x in val) {
                if (val.hasOwnProperty(x)) {
                    obj[x] = val[x];
                }
            }
        });
        return obj;
    }
    getId(component) {
        return (component + '').toLowerCase();
    }
}
SimpleModalHelper.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalHelper, deps: [{ token: 'ComponentRefs', optional: true }, { token: DOCUMENT }, { token: i0.ComponentFactoryResolver }, { token: i0.ApplicationRef }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
SimpleModalHelper.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalHelper, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalHelper, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['ComponentRefs']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.ComponentFactoryResolver }, { type: i0.ApplicationRef }, { type: i0.Injector }]; } });

const SimpleModalKeys = {
    OVERRIDE: 'OVERRIDE',
    OPEN_MODAL: 'OPEN_MODAL',
    CLOSE_MODAL: 'CLOSE_MODAL',
    HIDE_MODAL: 'HIDE_MODAL',
    SHOW_MODAL: 'SHOW_MODAL',
    LAST_ID: 'LAST_ID',
    LAST_Z_INDEX: 'LAST_Z_INDEX'
};

var T_MODAL_POSITION;
(function (T_MODAL_POSITION) {
    T_MODAL_POSITION["LEFT"] = "left";
    T_MODAL_POSITION["RIGHT"] = "right";
    T_MODAL_POSITION["TOP"] = "top";
    T_MODAL_POSITION["BOTTOM"] = "bottom";
    T_MODAL_POSITION["CENTER"] = "center";
})(T_MODAL_POSITION || (T_MODAL_POSITION = {}));
var T_DEFAULT_VALUES;
(function (T_DEFAULT_VALUES) {
    T_DEFAULT_VALUES["ALL_OF_THEM"] = "ALL_OF_THEM";
    T_DEFAULT_VALUES["OVERRIDE"] = "OVERRIDE";
})(T_DEFAULT_VALUES || (T_DEFAULT_VALUES = {}));
var T_DEVICE_TYPE;
(function (T_DEVICE_TYPE) {
    T_DEVICE_TYPE["MOBILE"] = "mobile";
    T_DEVICE_TYPE["TABLET"] = "tablet";
    T_DEVICE_TYPE["DESKTOP"] = "desktop";
    T_DEVICE_TYPE["CENTER"] = "center";
})(T_DEVICE_TYPE || (T_DEVICE_TYPE = {}));

class SimpleModalComponent {
    constructor(broadCaster, helper, changeDetector, scrollService, vp, localStorage, window) {
        this.broadCaster = broadCaster;
        this.helper = helper;
        this.changeDetector = changeDetector;
        this.scrollService = scrollService;
        this.vp = vp;
        this.localStorage = localStorage;
        this.window = window;
        this.isHidden = false;
        this.isShade = true;
        this.position = T_MODAL_POSITION.CENTER;
        this.subscriptions = new Subscription();
        this.config = {
            resolution: {
                mobile: 640,
                tablet: 970
            },
            defValues: {
                mobile: {
                    width: 80,
                    height: 20
                },
                tablet: {
                    width: 50,
                    height: 15
                },
                desktop: {
                    width: 30,
                    height: 10
                },
                center: {
                    width: 100,
                    height: 100
                },
                isPercent: true
            }
        };
        this._window = this.window;
    }
    keyup(event) {
        if ((this.helper.dispatchForCode(event) + '').toLowerCase() === 'escape') {
            const data = {
                id: this.modalId
            };
            this.broadCaster.emit(SimpleModalKeys.CLOSE_MODAL, data);
        }
    }
    ngAfterViewInit() {
        this.scrollService.lockScroll();
        this.refs = this.helper.createComponent(this.execute.component, this.execute.data, this.container);
        this.initSubscriptions();
        this.initData();
    }
    initData() {
        const lastZIndex = this.localStorage.getItem(SimpleModalKeys.LAST_Z_INDEX) || 0;
        this.parentCss = {
            zIndex: 1000 + parseInt(lastZIndex, 10) + 1
        };
        this.childCss = {};
        this.localStorage.setItem(SimpleModalKeys.LAST_Z_INDEX, lastZIndex);
        const screenWidth = this._window.innerWidth;
        const screenHeight = this._window.innerHeight;
        const getDimensionValue = (fieldName) => {
            const obj = this.dimensions && this.dimensions[deviceType]
                ? this.dimensions[deviceType]
                : this.config.defValues[deviceType];
            return obj ? obj[fieldName] : undefined;
        };
        const isPercent = !(this.dimensions && this.dimensions.isPercent === false);
        const getPxValue = (org, value) => {
            return isPercent ? value : (org * value) / 100;
        };
        let deviceType;
        const cssObj = {};
        if (this.position === T_MODAL_POSITION.CENTER) {
            deviceType = T_DEVICE_TYPE.CENTER;
        }
        else {
            deviceType =
                screenWidth <= this.config.resolution.mobile
                    ? T_DEVICE_TYPE.MOBILE
                    : screenWidth <= this.config.resolution.tablet
                        ? T_DEVICE_TYPE.TABLET
                        : T_DEVICE_TYPE.DESKTOP;
        }
        if (this.position === T_MODAL_POSITION.CENTER) {
            cssObj.width = getDimensionValue('width');
            cssObj.height = getDimensionValue('height');
        }
        else {
            if (this.position === T_MODAL_POSITION.RIGHT ||
                this.position === T_MODAL_POSITION.LEFT) {
                cssObj.height = !isPercent ? screenWidth : 100;
                cssObj.width = getDimensionValue('width');
            }
            if (this.position === T_MODAL_POSITION.TOP ||
                this.position === T_MODAL_POSITION.BOTTOM) {
                cssObj.width = !isPercent ? screenWidth : 100;
                cssObj.height = getDimensionValue('height');
            }
        }
        if (this.position === T_MODAL_POSITION.CENTER) {
            const getPosition = (field, org) => {
                return ((isPercent ? 100 : org) - getDimensionValue(field)) / 2;
            };
            cssObj.top = getPxValue(screenHeight, getPosition('height', screenHeight));
            cssObj.left = getPxValue(screenWidth, getPosition('width', screenWidth));
        }
        if (this.position === T_MODAL_POSITION.RIGHT ||
            this.position === T_MODAL_POSITION.LEFT) {
            cssObj[this.position === T_MODAL_POSITION.RIGHT ? 'right' : 'left'] = 0;
        }
        if (this.position === T_MODAL_POSITION.BOTTOM ||
            this.position === T_MODAL_POSITION.TOP) {
            cssObj[this.position === T_MODAL_POSITION.BOTTOM ? 'bottom' : 'top'] = 0;
        }
        ['width', 'height', 'top', 'left', 'right', 'bottom', 'z-index'].forEach((field) => {
            if (cssObj.hasOwnProperty(field)) {
                if (field === 'z-index') {
                    this.parentCss.zIndex = cssObj[field];
                }
                else {
                    this.childCss[field] = `${cssObj[field]}${field === 'width' || field === 'height'
                        ? isPercent
                            ? '%'
                            : 'px'
                        : ''}`;
                }
            }
        });
        this.changeDetector.detectChanges();
    }
    initSubscriptions() {
        this.subscriptions.add(this.broadCaster
            .on(SimpleModalKeys.HIDE_MODAL)
            .subscribe((val) => {
            if (val.exceptId === T_DEFAULT_VALUES.ALL_OF_THEM) {
                this.isHidden = true;
            }
            else {
                if (this.modalId !== val.exceptId) {
                    this.isHidden = true;
                }
            }
        }));
        this.subscriptions.add(this.broadCaster
            .on(SimpleModalKeys.SHOW_MODAL)
            .subscribe((val) => {
            if (val.id === T_DEFAULT_VALUES.ALL_OF_THEM) {
                this.isHidden = false;
            }
            else {
                if (this.modalId !== val.id) {
                    this.isHidden = false;
                }
            }
        }));
        this.subscriptions.add(this.vp.viewport$.subscribe(() => {
            this.initData();
        }));
    }
    ngOnDestroy() {
        this.scrollService.unlockScroll();
        this.subscriptions.unsubscribe();
    }
}
SimpleModalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalComponent, deps: [{ token: SimpleModalBroadcaster }, { token: SimpleModalHelper }, { token: i0.ChangeDetectorRef }, { token: i3.LockScrollService }, { token: i4.ViewportService }, { token: i5.LocalStorageService }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Component });
SimpleModalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: SimpleModalComponent, selector: "upr-simple-modal", host: { listeners: { "document:keyup": "keyup($event)" } }, viewQueries: [{ propertyName: "container", first: true, predicate: ["container"], descendants: true, read: ViewContainerRef }], ngImport: i0, template: "<div id=\"simple-modal\" [ngStyle]=\"parentCss\" *ngIf=\"!isHidden\">\r\n\t<div class=\"simple-modal-body\" [ngStyle]=\"childCss\">\r\n\t\t<span #container></span>\r\n\t</div>\r\n\t<div *ngIf=\"!isHidden && isShade\" class=\"simple-modal-shade\"></div>\r\n</div>\r\n", styles: [".outline-style-primary-blue{outline:1px dashed #0368d9;outline-offset:2px}.outline-style-primary-white{outline:1px dashed #fff;outline-offset:2px}.font-12{font-size:12px!important}.font-14{font-size:14px!important}.font-16{font-size:16px!important}.font-18{font-size:18px!important}.font-20{font-size:20px!important}.font-22{font-size:22px!important}.font-24{font-size:24px!important}.font-26{font-size:26px!important}.font-28{font-size:28px!important}.font-30{font-size:30px!important}.font-32{font-size:32px!important}.fade-me-in{animation:fadeIn .25s;-webkit-animation:fadeIn .25s;-moz-animation:fadeIn .25s;-o-animation:fadeIn .25s}@keyframes fadeIn{0%{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}@keyframes fadeIn{0%{opacity:0;-webkit-transform:scale(.95)}to{opacity:1;-webkit-transform:scale(1)}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}#simple-modal{position:fixed;width:100%;height:100%;top:0;bottom:0;z-index:999}.simple-modal-shade{position:absolute;top:0;bottom:0;width:100%;height:100%;background:#333;opacity:.6}.simple-modal-body{position:absolute;width:auto;height:auto;z-index:1000;overflow:auto}\n"], directives: [{ type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i6.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'upr-simple-modal', template: "<div id=\"simple-modal\" [ngStyle]=\"parentCss\" *ngIf=\"!isHidden\">\r\n\t<div class=\"simple-modal-body\" [ngStyle]=\"childCss\">\r\n\t\t<span #container></span>\r\n\t</div>\r\n\t<div *ngIf=\"!isHidden && isShade\" class=\"simple-modal-shade\"></div>\r\n</div>\r\n", styles: [".outline-style-primary-blue{outline:1px dashed #0368d9;outline-offset:2px}.outline-style-primary-white{outline:1px dashed #fff;outline-offset:2px}.font-12{font-size:12px!important}.font-14{font-size:14px!important}.font-16{font-size:16px!important}.font-18{font-size:18px!important}.font-20{font-size:20px!important}.font-22{font-size:22px!important}.font-24{font-size:24px!important}.font-26{font-size:26px!important}.font-28{font-size:28px!important}.font-30{font-size:30px!important}.font-32{font-size:32px!important}.fade-me-in{animation:fadeIn .25s;-webkit-animation:fadeIn .25s;-moz-animation:fadeIn .25s;-o-animation:fadeIn .25s}@keyframes fadeIn{0%{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}@keyframes fadeIn{0%{opacity:0;-webkit-transform:scale(.95)}to{opacity:1;-webkit-transform:scale(1)}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}#simple-modal{position:fixed;width:100%;height:100%;top:0;bottom:0;z-index:999}.simple-modal-shade{position:absolute;top:0;bottom:0;width:100%;height:100%;background:#333;opacity:.6}.simple-modal-body{position:absolute;width:auto;height:auto;z-index:1000;overflow:auto}\n"] }]
        }], ctorParameters: function () { return [{ type: SimpleModalBroadcaster }, { type: SimpleModalHelper }, { type: i0.ChangeDetectorRef }, { type: i3.LockScrollService }, { type: i4.ViewportService }, { type: i5.LocalStorageService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }]; }, propDecorators: { container: [{
                type: ViewChild,
                args: ['container', { read: ViewContainerRef }]
            }], keyup: [{
                type: HostListener,
                args: ['document:keyup', ['$event']]
            }] } });

class SimpleModalModule {
}
SimpleModalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SimpleModalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalModule, declarations: [SimpleModalComponent], imports: [CommonModule], exports: [SimpleModalComponent] });
SimpleModalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalModule, providers: [SimpleModalBroadcaster, SimpleModalHelper], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [SimpleModalComponent],
                    exports: [SimpleModalComponent],
                    providers: [SimpleModalBroadcaster, SimpleModalHelper]
                }]
        }] });

class SimpleModalService {
    constructor(helper, broadCaster, localStorage) {
        this.helper = helper;
        this.broadCaster = broadCaster;
        this.localStorage = localStorage;
        this.simpleModal$ = new BehaviorSubject(null);
        this.refs = {};
    }
    show(id) {
        if (id) {
            if (typeof id === 'string') {
                this.setLastId(id);
            }
        }
        else {
            id = this.getLastId();
        }
        this.hide(T_DEFAULT_VALUES.ALL_OF_THEM);
        const data = {
            id: !id || typeof id === 'boolean' ? T_DEFAULT_VALUES.ALL_OF_THEM : id
        };
        this.broadCaster.emit(SimpleModalKeys.SHOW_MODAL, data);
    }
    hide(id) {
        if (id) {
            if (typeof id === 'string') {
                this.setLastId(id);
            }
        }
        else {
            id = this.getLastId();
        }
        const data = {
            exceptId: !id || typeof id === 'boolean' ? T_DEFAULT_VALUES.ALL_OF_THEM : id
        };
        this.broadCaster.emit(SimpleModalKeys.HIDE_MODAL, data);
    }
    close(id) {
        if (!id || typeof id !== 'boolean') {
            id = this.getLastId();
        }
        this.destroyModal({
            id: !id || typeof id === 'boolean' ? T_DEFAULT_VALUES.ALL_OF_THEM : id
        });
    }
    open(component, data, params) {
        if (data) {
            this.simpleModal$.next(data);
        }
        if (this.helper.sizeOf(this.refs) < 1) {
            this.localStorage.setItem(SimpleModalKeys.LAST_ID, null);
            this.subscriptions = new Subscription();
            this.subscriptions.add(this.broadCaster
                .on(SimpleModalKeys.CLOSE_MODAL)
                .subscribe((_val) => {
                this.destroyModal(_val);
            }));
        }
        return this.createModal(component, data, params);
    }
    setLastId(id, exclude = false) {
        const obj = {};
        let cacheData = this.localStorage.getItem(SimpleModalKeys.LAST_ID);
        if (cacheData) {
            cacheData = JSON.parse(cacheData);
        }
        if (cacheData) {
            for (const x in cacheData) {
                if (cacheData.hasOwnProperty(x) && x !== id) {
                    obj[x] = x;
                }
            }
        }
        if (!exclude) {
            obj[id] = id;
        }
        if (this.helper.sizeOf(obj) > 0) {
            this.localStorage.setItem(SimpleModalKeys.LAST_ID, obj);
        }
        return { id: id };
    }
    getLastId() {
        let id;
        let cacheData = this.localStorage.getItem(SimpleModalKeys.LAST_ID);
        if (cacheData) {
            cacheData = JSON.parse(cacheData);
        }
        if (cacheData) {
            const keys = Object.keys(cacheData);
            if (keys) {
                id = keys[keys.length - 1];
            }
        }
        return id ? id : '';
    }
    createModal(component, data, params) {
        const modalData = this.setLastId(this.helper.getId(component));
        const serviceData = {
            modalId: modalData.id,
            execute: {
                component: component,
                data: data
            }
        };
        this.refs[modalData.id] = this.helper.createComponent(SimpleModalComponent, this.helper.merge(serviceData, params));
        return modalData.id;
    }
    destroyModal(data) {
        if (data.id === T_DEFAULT_VALUES.ALL_OF_THEM) {
            for (const x in this.refs) {
                if (this.refs.hasOwnProperty(x)) {
                    this.refs[x].destroy();
                    this.setLastId(data.id, true);
                }
            }
        }
        else {
            this.refs[data.id].destroy();
            this.setLastId(data.id, true);
        }
        if (this.helper.sizeOf(this.refs) < 1) {
            this.subscriptions.unsubscribe();
            this.localStorage.setItem(SimpleModalKeys.LAST_Z_INDEX, 0);
        }
    }
}
SimpleModalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalService, deps: [{ token: SimpleModalHelper }, { token: SimpleModalBroadcaster }, { token: i5.LocalStorageService }], target: i0.ɵɵFactoryTarget.Injectable });
SimpleModalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SimpleModalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: SimpleModalHelper }, { type: SimpleModalBroadcaster }, { type: i5.LocalStorageService }]; } });

export { SimpleModalComponent, SimpleModalModule, SimpleModalService, T_DEFAULT_VALUES, T_DEVICE_TYPE, T_MODAL_POSITION };
